import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { signupAgency, loginAgency, extractToken } from "../../lib/masterApi";
import { extractApiErrorMessage } from "../../lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Inline signup / login shown when a guest hits "Choose Plan".
export default function AuthModal({ open, planName, isFree, onClose, onAuthenticated }) {
  const [mode, setMode] = useState("signup");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const isSignup = mode === "signup";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (isSignup && form.name.trim().length < 2) return setError("Please enter your name or agency name.");
    if (!EMAIL_RE.test(form.email)) return setError("Please enter a valid email address.");
    if (form.password.length < 8) return setError("Password must be at least 8 characters.");
    setError("");
    setLoading(true);
    try {
      const data = isSignup
        ? await signupAgency({ ...form, name: form.name.trim(), phone: form.phone.trim() })
        : await loginAgency(form);
      const token = extractToken(data);
      if (!token) throw new Error("No session token returned.");
      onAuthenticated(token);
    } catch (err) {
      setError(err?.response ? extractApiErrorMessage(err) : err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !loading && onClose()}>
      <DialogContent className="max-w-md" data-testid="plans-auth-modal">
        <DialogHeader>
          <DialogTitle>{isSignup ? "Create your agency account" : "Log in to continue"}</DialogTitle>
          <DialogDescription>
            {isFree
              ? "Then start your free trial. No credit card required."
              : planName
                ? `Then pay securely to activate ${planName}.`
                : "Then pay securely to activate your plan."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" noValidate>
          {isSignup && (
            <Input placeholder="Name / agency name" autoComplete="name" value={form.name} onChange={set("name")} data-testid="auth-name" />
          )}
          <Input type="email" placeholder="Email" autoComplete="email" value={form.email} onChange={set("email")} data-testid="auth-email" />
          {isSignup && (
            <Input type="tel" placeholder="Phone" autoComplete="tel" value={form.phone} onChange={set("phone")} data-testid="auth-phone" />
          )}
          <Input
            type="password"
            placeholder="Password (min 8 characters)"
            autoComplete={isSignup ? "new-password" : "current-password"}
            value={form.password}
            onChange={set("password")}
            data-testid="auth-password"
          />
          {error && (
            <p role="alert" className="text-sm text-red-600" data-testid="auth-error">
              {error}
            </p>
          )}
          <Button type="submit" disabled={loading} className="w-full bg-[#FF6B00] hover:bg-[#e55f00] text-white" data-testid="auth-submit">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSignup ? "Signup and Continue" : "Login and Continue"}
          </Button>
        </form>
        <p className="text-center text-sm text-slate-600">
          {isSignup ? "Already have an account?" : "New here?"}{" "}
          <button
            type="button"
            className="font-semibold text-[#FF6B00] hover:underline"
            onClick={() => {
              setMode(isSignup ? "login" : "signup");
              setError("");
            }}
            data-testid="auth-toggle"
          >
            {isSignup ? "Log in" : "Sign up"}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
}
